import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Wallet, ArrowDownCircle, ArrowUpCircle, RefreshCw, Filter, X,
  DollarSign, TrendingUp, TrendingDown, Clock, CheckCircle2, XCircle, Save, Loader2,
  CreditCard, Smartphone, Building2, Zap,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

const TX_TYPES = [
  { v: "", label: "Tous" },
  { v: "deposit", label: "Dépôts" },
  { v: "withdraw", label: "Retraits" },
  { v: "commission", label: "Commissions" },
  { v: "transfer", label: "Transferts" },
  { v: "refund", label: "Remboursements" },
];
const STATUSES = [
  { v: "", label: "Tous statuts" },
  { v: "completed", label: "Complété" },
  { v: "pending", label: "En attente" },
  { v: "failed", label: "Échoué" },
  { v: "reversed", label: "Annulé" },
];

const KYC_BANNER = {
  not_started: {
    color: "#B45309", bg: "rgba(180,83,9,0.10)",
    title: "🛡️ Vérification d'identité requise",
    body: "Complétez votre KYC/KYB pour débloquer les retraits et l'Escrow AFRICTAL.",
  },
  pending: {
    color: "#B45309", bg: "rgba(180,83,9,0.10)",
    title: "🕐 Dossier en cours d'examen",
    body: "Votre vérification est en cours (24–72 h). Les retraits seront activés dès approbation.",
  },
  rejected: {
    color: "#B91C1C", bg: "rgba(185,28,28,0.08)",
    title: "❌ Dossier refusé",
    body: "Consultez le motif et soumettez à nouveau vos documents.",
  },
};

/**
 * MyWallet — /mon-wallet · Solde + Historique + Dépôt + Retrait.
 * Additif : n'interfère pas avec /wallet existant.
 */
export default function MyWallet() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ credits: 0, debits: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [openDeposit, setOpenDeposit] = useState(false);
  const [openWithdraw, setOpenWithdraw] = useState(false);
  const [config, setConfig] = useState(null);
  const [kycStatus, setKycStatus] = useState(null); // V3.7 : gate retraits
  const [filters, setFilters] = useState({ type: "", status: "", date_from: "", date_to: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [w, cfg, kyc] = await Promise.all([
        api.get("/v1/payments/wallet/transactions", { params }),
        api.get("/v1/payments/config"),
        api.get("/v1/kyc/me").catch(() => ({ data: { verification: { status: "not_started" } } })),
      ]);
      setWallet(w.data.wallet);
      setItems(w.data.items || []);
      setTotals(w.data.totals || {});
      setConfig(cfg.data.config);
      setKycStatus(kyc.data?.verification?.status || "not_started");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (user?.id) load(); }, [user?.id, load]);

  const cur = wallet?.currency || "XOF";

  return (
    <PrivateLayout title="💳 Mon Wallet AFRICTAL" data-testid="mywallet-page">
      {loading && !wallet ? (
        <div data-testid="mywallet-loading">Chargement…</div>
      ) : (
        <>
          {/* Banner KYC — V3.7 gate retrait */}
          {kycStatus && kycStatus !== "approved" && (
            <article className="afr-card" data-testid={`wallet-kyc-banner-${kycStatus}`} style={{
              padding: 14, marginBottom: 14,
              borderLeft: `4px solid ${KYC_BANNER[kycStatus].color}`,
              background: KYC_BANNER[kycStatus].bg,
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            }}>
              <div style={{ flex: "1 1 260px" }}>
                <div style={{ fontWeight: 700, color: KYC_BANNER[kycStatus].color }}>
                  {KYC_BANNER[kycStatus].title}
                </div>
                <div className="afr-muted" style={{ fontSize: "0.82rem", marginTop: 2 }}>
                  {KYC_BANNER[kycStatus].body}
                </div>
              </div>
              <a href="/mon-kyc" className="afr-btn afr-btn--primary afr-btn--sm" data-testid="wallet-kyc-cta">
                {kycStatus === "pending" ? "Suivre mon dossier" : "Compléter la vérification"}
              </a>
            </article>
          )}

          {/* Solde principal */}
          <article className="afr-card" data-testid="wallet-balance-card" style={{
            padding: 24, marginBottom: 20,
            background: "linear-gradient(135deg, #009640, #0F2A42)",
            color: "#fff",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
              <div>
                <div style={{ fontSize: "0.86rem", opacity: 0.8, marginBottom: 4 }}>
                  <Wallet size={14} style={{ verticalAlign: "middle" }} /> Solde disponible
                </div>
                <div style={{ fontSize: "2.2rem", fontWeight: 700 }} data-testid="wallet-balance">
                  {fmt(wallet?.balance_available, cur)}
                </div>
                <div style={{ opacity: 0.75, fontSize: "0.82rem", marginTop: 6 }}>
                  Bloqué : {fmt(wallet?.balance_locked || 0, cur)} · Revenus totaux : {fmt(wallet?.total_income, cur)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="afr-btn afr-btn--secondary" onClick={() => setOpenDeposit(true)} data-testid="btn-deposit">
                  <ArrowDownCircle size={14} /> Déposer
                </button>
                <button className="afr-btn afr-btn--outline"
                  style={{ borderColor: "#fff", color: "#fff", opacity: kycStatus !== "approved" ? 0.6 : 1 }}
                  onClick={() => {
                    if (kycStatus !== "approved") {
                      toast.error("Vérification d'identité requise · Rendez-vous sur /mon-kyc");
                      return;
                    }
                    setOpenWithdraw(true);
                  }}
                  data-testid="btn-withdraw"
                  title={kycStatus !== "approved" ? "KYC/KYB requis pour retirer" : undefined}
                >
                  <ArrowUpCircle size={14} /> Retirer
                </button>
              </div>
            </div>
          </article>

          {/* KPIs */}
          <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 16 }} data-testid="wallet-kpis">
            <SmallKpi Icon={TrendingUp} label="Crédits (filtrés)" value={fmt(totals.credits, cur)} accent="#15803D" />
            <SmallKpi Icon={TrendingDown} label="Débits (filtrés)" value={fmt(totals.debits, cur)} accent="#B45309" />
            <SmallKpi Icon={RefreshCw} label="Transactions" value={totals.count} accent="#0F2A42" />
          </div>

          {/* Filtres */}
          <div className="afr-card" style={{ padding: 12, marginBottom: 14 }} data-testid="wallet-filters">
            <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
              <FilterSelect label="Type" value={filters.type} onChange={(v) => setFilters((f) => ({ ...f, type: v }))} options={TX_TYPES} testid="filter-type" Icon={Filter} />
              <FilterSelect label="Statut" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={STATUSES} testid="filter-status" Icon={CheckCircle2} />
              <FilterInput label="Du" type="date" value={filters.date_from} onChange={(v) => setFilters((f) => ({ ...f, date_from: v }))} testid="filter-from" />
              <FilterInput label="Au" type="date" value={filters.date_to} onChange={(v) => setFilters((f) => ({ ...f, date_to: v }))} testid="filter-to" />
            </div>
          </div>

          {/* Historique */}
          {items.length === 0 ? (
            <div className="afr-empty" data-testid="wallet-empty">
              <div className="afr-empty__title">Aucune transaction</div>
              <div className="afr-empty__lead">Vos dépôts, retraits et commissions apparaîtront ici.</div>
            </div>
          ) : (
            <table className="afr-table" data-testid="wallet-transactions">
              <thead>
                <tr>
                  <th>Date</th><th>Type</th><th>Description</th>
                  <th>Provider</th><th style={{ textAlign: "right" }}>Montant</th><th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id} data-testid={`tx-${t.id}`}>
                    <td style={{ fontSize: "0.78rem" }}>{new Date(t.created_at).toLocaleString("fr-FR")}</td>
                    <td><TxTypeChip type={t.type} direction={t.direction} /></td>
                    <td style={{ fontSize: "0.86rem" }}>{describeTx(t)}</td>
                    <td style={{ fontSize: "0.82rem", textTransform: "capitalize" }}>{t.provider}</td>
                    <td style={{ textAlign: "right", fontWeight: 700, color: t.direction === "credit" ? "#15803D" : "#B45309" }}>
                      {t.direction === "credit" ? "+" : "−"} {fmt(t.amount, t.currency)}
                    </td>
                    <td><StatusChip status={t.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {openDeposit && <DepositModal config={config} onClose={() => { setOpenDeposit(false); load(); }} />}
      {openWithdraw && <WithdrawModal config={config} wallet={wallet} onClose={() => { setOpenWithdraw(false); load(); }} />}
    </PrivateLayout>
  );
}

function DepositModal({ config, onClose }) {
  const [form, setForm] = useState({ amount: "", currency: "XOF", provider: "flutterwave", operator: "orange", phone: "" });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const providers = config?.providers || {};
  const momoOps = providers.mobile_money?.operators || {};

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Montant invalide");
    setSaving(true);
    try {
      const payload = { amount, currency: form.currency, provider: form.provider };
      if (form.provider === "mobile_money") {
        payload.operator = form.operator;
        payload.phone = form.phone;
      }
      const { data } = await api.post("/v1/payments/wallet/deposit", payload);
      setResult(data);
      if (data.intent?.status === "not_configured") {
        toast.info(data.intent.message || "Provider en attente de configuration");
      } else {
        toast.success("Dépôt initié");
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} title="Déposer sur mon Wallet" testid="deposit-modal">
      {result ? (
        <div data-testid="deposit-result">
          <div className="afr-card" style={{ padding: 14, background: "rgba(0,150,64,0.06)", borderLeft: "3px solid #009640", marginBottom: 12 }}>
            <strong>Transaction créée · {result.transaction.status}</strong>
            <div className="afr-muted" style={{ fontSize: "0.86rem", marginTop: 4 }}>
              Réf. {result.transaction.provider_ref || result.transaction.id}
            </div>
            {result.intent?.message && <div style={{ fontSize: "0.86rem", marginTop: 8 }}>{result.intent.message}</div>}
          </div>
          <button className="afr-btn afr-btn--primary" onClick={onClose}>Fermer</button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <p className="afr-muted" style={{ fontSize: "0.86rem", marginBottom: 12 }}>Choisissez un moyen de paiement</p>
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }} data-testid="provider-choice">
            {[
              { v: "wallet", label: "Solde AFRICTAL (instantané)", Icon: Zap, enabled: providers.wallet?.enabled !== false },
              { v: "flutterwave", label: "Carte bancaire", Icon: CreditCard, enabled: providers.flutterwave?.enabled },
              { v: "mobile_money", label: "Mobile Money", Icon: Smartphone, enabled: providers.mobile_money?.enabled },
            ].map((p) => (
              <button
                key={p.v} type="button" onClick={() => setForm((f) => ({ ...f, provider: p.v }))}
                className={`afr-btn afr-btn--sm ${form.provider === p.v ? "afr-btn--primary" : "afr-btn--outline"}`}
                data-testid={`provider-${p.v}`}
              >
                <p.Icon size={12} /> {p.label} {!p.enabled && "(bientôt)"}
              </button>
            ))}
          </div>

          {form.provider === "mobile_money" && (
            <div className="afr-card" style={{ padding: 10, marginBottom: 12 }} data-testid="momo-selector">
              <span className="afr-muted" style={{ fontSize: "0.78rem" }}>Opérateur</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                {Object.entries(momoOps).map(([k, v]) => (
                  <button
                    key={k} type="button" onClick={() => setForm((f) => ({ ...f, operator: k }))}
                    className={`afr-btn afr-btn--sm ${form.operator === k ? "afr-btn--primary" : "afr-btn--outline"}`}
                    data-testid={`momo-${k}`}
                    disabled={!v.enabled}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              <input placeholder="Numéro (+221...)" value={form.phone} onChange={set("phone")} data-testid="momo-phone" style={{ marginTop: 8, width: "100%", border: "none", outline: "none", background: "transparent", fontFamily: "'Outfit', sans-serif" }} />
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8 }}>
            <Row><input required type="number" min="1" step="1" placeholder="Montant" value={form.amount} onChange={set("amount")} data-testid="deposit-amount" /></Row>
            <Row><input value={form.currency} onChange={set("currency")} data-testid="deposit-currency" /></Row>
          </div>

          <button type="submit" className="afr-btn afr-btn--primary" disabled={saving} data-testid="deposit-submit">
            {saving ? <Loader2 size={14} className="afr-spin" /> : <ArrowDownCircle size={14} />} Confirmer le dépôt
          </button>
        </form>
      )}
    </Modal>
  );
}

function WithdrawModal({ config, wallet, onClose }) {
  const [form, setForm] = useState({ amount: "", method: "mobile_money", operator: "orange", account_number: "", account_holder: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const momoOps = config?.providers?.mobile_money?.operators || {};
  const available = wallet?.balance_available || 0;

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Montant invalide");
    if (amount > available) return toast.error(`Solde insuffisant. Disponible : ${fmt(available, wallet?.currency)}`);
    if (!form.account_number || form.account_number.length < 4) return toast.error("Numéro de destination requis");
    setSaving(true);
    try {
      const payload = {
        amount, method: form.method,
        currency: wallet?.currency || "XOF",
        account_number: form.account_number, account_holder: form.account_holder, notes: form.notes,
      };
      if (form.method === "mobile_money") payload.operator = form.operator;
      await api.post("/v1/payments/wallet/withdraw", payload);
      toast.success("Retrait demandé · en attente de validation");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} title="Demander un retrait" testid="withdraw-modal">
      <div className="afr-card" style={{ padding: 12, marginBottom: 14, background: "rgba(15,42,66,0.04)" }}>
        <div className="afr-muted" style={{ fontSize: "0.82rem" }}>Solde disponible</div>
        <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>{fmt(available, wallet?.currency)}</div>
      </div>
      <form onSubmit={submit}>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }} data-testid="method-choice">
          {[
            { v: "mobile_money", label: "Mobile Money", Icon: Smartphone, disabled: false },
            { v: "bank", label: "Compte bancaire", Icon: Building2, disabled: true },
          ].map((m) => (
            <button
              key={m.v} type="button" onClick={() => !m.disabled && setForm((f) => ({ ...f, method: m.v }))}
              className={`afr-btn afr-btn--sm ${form.method === m.v ? "afr-btn--primary" : "afr-btn--outline"}`}
              disabled={m.disabled}
              data-testid={`method-${m.v}`}
            >
              <m.Icon size={12} /> {m.label} {m.disabled && "(bientôt)"}
            </button>
          ))}
        </div>

        {form.method === "mobile_money" && (
          <div className="afr-card" style={{ padding: 10, marginBottom: 12 }}>
            <span className="afr-muted" style={{ fontSize: "0.78rem" }}>Opérateur destination</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
              {Object.entries(momoOps).map(([k, v]) => (
                <button
                  key={k} type="button" onClick={() => setForm((f) => ({ ...f, operator: k }))}
                  className={`afr-btn afr-btn--sm ${form.operator === k ? "afr-btn--primary" : "afr-btn--outline"}`}
                  disabled={!v.enabled}
                  data-testid={`withdraw-op-${k}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <Row><input required type="number" min="1" step="1" placeholder="Montant à retirer" value={form.amount} onChange={set("amount")} data-testid="withdraw-amount" /></Row>
        <Row><input required placeholder="Numéro (ou IBAN) *" value={form.account_number} onChange={set("account_number")} data-testid="withdraw-account" /></Row>
        <Row><input placeholder="Titulaire du compte" value={form.account_holder} onChange={set("account_holder")} data-testid="withdraw-holder" /></Row>
        <Row><textarea placeholder="Notes (optionnel)" rows={2} value={form.notes} onChange={set("notes")} data-testid="withdraw-notes" /></Row>

        <button type="submit" className="afr-btn afr-btn--primary" disabled={saving} data-testid="withdraw-submit">
          {saving ? <Loader2 size={14} className="afr-spin" /> : <ArrowUpCircle size={14} />} Envoyer la demande
        </button>
      </form>
    </Modal>
  );
}

function Modal({ onClose, title, children, testid }) {
  return (
    <div role="dialog" data-testid={testid} onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,42,66,0.5)", display: "grid", placeItems: "center", zIndex: 90, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, maxWidth: 560, width: "100%", padding: 20, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <strong style={{ fontSize: "1.05rem" }}>{title}</strong>
          <button type="button" onClick={onClose} aria-label="Fermer" style={{ border: 0, background: "transparent", cursor: "pointer" }}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Row({ children }) {
  return (
    <div className="afr-card" style={{ padding: "0.7rem", marginBottom: "0.6rem" }}>
      <style>{`.afr-card input, .afr-card textarea, .afr-card select { width:100%; border:none; outline:none; background:transparent; font-family:'Outfit',sans-serif; font-size:0.95rem; color:var(--afr-secondary); }`}</style>
      {children}
    </div>
  );
}

function SmallKpi({ Icon, label, value, accent }) {
  return (
    <div className="afr-card" style={{ padding: 10, borderLeft: `3px solid ${accent}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="afr-muted" style={{ fontSize: "0.74rem" }}>{label}</div>
        {Icon && <Icon size={14} color={accent} />}
      </div>
      <div style={{ fontSize: "1.05rem", fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid, Icon }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, color: "#64748b", marginBottom: 4, fontWeight: 700, textTransform: "uppercase" }}>
        {Icon && <Icon size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />}{label}
      </label>
      <select className="afr-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </div>
  );
}

function FilterInput({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, color: "#64748b", marginBottom: 4, fontWeight: 700, textTransform: "uppercase" }}>{label}</label>
      <input type={type} className="afr-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function TxTypeChip({ type, direction }) {
  const map = {
    deposit: { label: "Dépôt", color: "#15803D", bg: "rgba(21,128,61,0.10)" },
    withdraw: { label: "Retrait", color: "#B45309", bg: "rgba(180,83,9,0.10)" },
    commission: { label: "Commission ⭐", color: "#B45309", bg: "rgba(180,83,9,0.10)" },
    transfer: { label: "Transfert", color: "#0F2A42", bg: "rgba(15,42,66,0.06)" },
    refund: { label: "Remboursement", color: "#0F2A42", bg: "rgba(15,42,66,0.06)" },
  };
  const m = map[type] || { label: type, color: "#64748b", bg: "rgba(100,116,139,0.10)" };
  return <span className="afr-badge" style={{ background: m.bg, color: m.color }}>{m.label}</span>;
}

function StatusChip({ status }) {
  const map = {
    completed: { label: "Complété", color: "#15803D", bg: "rgba(21,128,61,0.10)", Icon: CheckCircle2 },
    pending:   { label: "En attente", color: "#B45309", bg: "rgba(180,83,9,0.10)", Icon: Clock },
    failed:    { label: "Échoué", color: "#B91C1C", bg: "rgba(185,28,28,0.10)", Icon: XCircle },
    reversed:  { label: "Annulé", color: "#64748b", bg: "rgba(100,116,139,0.10)", Icon: RefreshCw },
  };
  const m = map[status] || { label: status, color: "#64748b", bg: "rgba(100,116,139,0.10)", Icon: Clock };
  const I = m.Icon;
  return <span className="afr-badge" style={{ background: m.bg, color: m.color }}><I size={11} /> {m.label}</span>;
}

function describeTx(t) {
  if (t.type === "commission") return `Mission « ${t.meta?.mission_title || "—"} »`;
  if (t.type === "deposit") return `Dépôt via ${t.provider}${t.meta?.operator ? ` · ${t.meta.operator}` : ""}`;
  if (t.type === "withdraw") return `Retrait vers ${t.provider}${t.meta?.operator ? ` · ${t.meta.operator}` : ""}`;
  return t.type;
}

function fmt(n, cur = "XOF") {
  if (n == null || Number.isNaN(n)) return "—";
  try { return `${new Intl.NumberFormat("fr-FR").format(n)} ${cur}`; } catch { return `${n} ${cur}`; }
}
